import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import MainCard from 'components/MainCard';
import navigation from 'menu-items';

// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import HomeFilled from '@ant-design/icons/HomeFilled';

// Constants
const DEFAULT_MAX_ITEMS = 8;
const BREADCRUMB_ARIA_LABEL = 'breadcrumb';
const DEFAULT_SEPARATOR = '/';
const HOME_TITLE = 'Home';
const BREADCRUMB_SPECIAL_PATH = '/components-overview/breadcrumbs';
const BREADCRUMB_FALLBACK_PATH = '/apps/customer/customer-card';

// Helper function to find navigation items
const findNavigationItems = (navigation, customLocation, setMain, setItem, getCollapse) => {
  for (const menu of navigation?.items || []) {
    if (menu.type === 'group') {
      if (menu?.url === customLocation) {
        setMain(menu);
        setItem(menu);
      } else {
        getCollapse(menu);
      }
    }
  }
};

// Helper function to handle collapse navigation
const handleCollapseNavigation = (menu, customLocation, setMain, setItem, custom) => {
  if (custom || !menu.children) return;

  for (const collapse of menu.children) {
    if (collapse.type === 'collapse') {
      handleCollapseNavigation(collapse, customLocation, setMain, setItem, custom);
      if (collapse.url === customLocation) {
        setMain(collapse);
        setItem(collapse);
      }
    } else if (collapse.type === 'item' && customLocation === collapse.url) {
      setMain(menu);
      setItem(collapse);
    }
  }
};

// Helper function to create home typography
const createHomeTypography = (icons, icon, iconSX) => (
  <Typography component={Link} to="/" color="text.secondary" variant="h6" sx={{ textDecoration: 'none' }}>
    {icons && <HomeOutlined style={iconSX} />}
    {icon && !icons && <HomeFilled style={{ ...iconSX, marginRight: 0 }} />}
    {(!icon || icons) && HOME_TITLE}
  </Typography>
);

// Helper function to create main content typography
const createMainContent = (main, icons, iconSX, currentPath) => {
  const CollapseIcon = main.icon || ApartmentOutlined;
  const isCurrentPage = currentPath === main.url;
  return (
    <Typography
      {...(main.url && { component: Link, to: main.url })}
      variant={isCurrentPage ? 'subtitle1' : 'h6'}
      sx={{ textDecoration: 'none' }}
      color={isCurrentPage ? 'text.primary' : 'text.secondary'}
    >
      {icons && <CollapseIcon style={iconSX} />}
      {main?.title}
    </Typography>
  );
};

// Helper function to create custom breadcrumbs
const createCustomBreadcrumbs = (links, maxItems, separatorIcon, iconSX) => (
  <MuiBreadcrumbs aria-label={BREADCRUMB_ARIA_LABEL} maxItems={maxItems || DEFAULT_MAX_ITEMS} separator={separatorIcon}>
    {links?.map((link, index) => {
      const CollapseIcon = link.icon || ApartmentOutlined;
      const isCurrentPage = !link.to;
      const linkKey = `breadcrumb-link-${link.title || index}`;
      return (
        <Typography
          key={linkKey}
          {...(link.to && { component: Link, to: link.to })}
          variant={isCurrentPage ? 'subtitle1' : 'h6'}
          sx={{ textDecoration: 'none' }}
          color={isCurrentPage ? 'text.primary' : 'text.secondary'}
        >
          {link.icon && <CollapseIcon style={iconSX} />}
          {link.title}
        </Typography>
      );
    })}
  </MuiBreadcrumbs>
);

// Helper function to create main card wrapper
const createMainCardWrapper = (children, card, sx, others, divider) => (
  <MainCard
    border={card}
    sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
    {...others}
    content={card}
    shadow="none"
  >
    {children}
    {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
  </MainCard>
);

// Helper function to create grid content
const createGridContent = (content, options) => {
  const { rightAlign, title, titleBottom, card, custom, heading, item } = options;
  return (
    <Grid
      container
      direction={rightAlign ? 'row' : 'column'}
      justifyContent={rightAlign ? 'space-between' : 'flex-start'}
      alignItems={rightAlign ? 'center' : 'flex-start'}
      spacing={1}
    >
      {title && !titleBottom && (
        <Grid>
          <Typography variant="h2">{custom ? heading : item?.title}</Typography>
        </Grid>
      )}
      <Grid>{content}</Grid>
      {title && titleBottom && (
        <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
          <Typography variant="h2">{custom ? heading : item?.title}</Typography>
        </Grid>
      )}
    </Grid>
  );
};

// Helper function to handle collapse item case
const handleCollapseItem = (main, options) => {
  const { custom, icons, iconSX, location, rightAlign, title, titleBottom, card, maxItems, separatorIcon, icon, sx, others, divider } =
    options;
  const mainContent = createMainContent(main, icons, iconSX, location.pathname);

  if (custom) {
    const breadcrumbContent = (
      <MuiBreadcrumbs aria-label={BREADCRUMB_ARIA_LABEL} maxItems={maxItems || DEFAULT_MAX_ITEMS} separator={separatorIcon}>
        {createHomeTypography(icons, icon, iconSX)}
        {mainContent}
      </MuiBreadcrumbs>
    );

    const gridContent = createGridContent(breadcrumbContent, {
      rightAlign,
      title,
      titleBottom,
      card,
      custom,
      heading: main.title,
      item: null
    });
    return createMainCardWrapper(gridContent, card, sx, others, divider);
  }

  return null;
};

// Helper function to handle regular item case
const handleRegularItem = (item, options) => {
  const {
    custom,
    links,
    main,
    icons,
    iconSX,
    location,
    maxItems,
    separatorIcon,
    icon,
    rightAlign,
    title,
    titleBottom,
    card,
    heading,
    sx,
    others,
    divider
  } = options;
  const ItemIcon = item?.icon || ApartmentOutlined;
  const itemContent = (
    <Typography variant="subtitle1" color="text.primary">
      {icons && <ItemIcon style={iconSX} />}
      {item?.title}
    </Typography>
  );

  const mainContent = main ? createMainContent(main, icons, iconSX, location.pathname) : null;

  let tempContent;
  if (custom && links?.length > 0) {
    tempContent = createCustomBreadcrumbs(links, maxItems, separatorIcon, iconSX);
  } else {
    tempContent = (
      <MuiBreadcrumbs aria-label={BREADCRUMB_ARIA_LABEL} maxItems={maxItems || DEFAULT_MAX_ITEMS} separator={separatorIcon}>
        {createHomeTypography(icons, icon, iconSX)}
        {mainContent}
        {itemContent}
      </MuiBreadcrumbs>
    );
  }

  if (item?.breadcrumbs !== false || custom) {
    const gridContent = createGridContent(tempContent, {
      rightAlign,
      title,
      titleBottom,
      card,
      custom,
      heading,
      item
    });
    return createMainCardWrapper(gridContent, card, sx, others, divider);
  }

  return null;
};

export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();
  const [main, setMain] = useState();
  const [item, setItem] = useState();

  const iconSX = {
    marginRight: theme.spacing(0.75),
    marginLeft: 0,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  const customLocation = location.pathname.includes(BREADCRUMB_SPECIAL_PATH) ? BREADCRUMB_FALLBACK_PATH : location.pathname;

  const getCollapse = React.useCallback(
    (menu) => {
      handleCollapseNavigation(menu, customLocation, setMain, setItem, custom);
    },
    [customLocation, custom]
  );

  useEffect(() => {
    findNavigationItems(navigation, customLocation, setMain, setItem, getCollapse);
  }, [customLocation, getCollapse]);

  const SeparatorIcon = separator;
  const separatorIcon = separator ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} /> : DEFAULT_SEPARATOR;
  // Handle collapse item case
  if (main?.type === 'collapse' && !main?.breadcrumbs) {
    const result = handleCollapseItem(main, {
      custom,
      icons,
      iconSX,
      location,
      rightAlign,
      title,
      titleBottom,
      card,
      maxItems,
      separatorIcon,
      icon,
      sx,
      others,
      divider
    });
    if (result) return result;
  }

  // Handle item case
  const shouldShowItem = item?.type === 'item' || (item?.type === 'group' && item?.url) || custom;

  if (shouldShowItem) {
    const result = handleRegularItem(item, {
      custom,
      links,
      main,
      icons,
      iconSX,
      location,
      maxItems,
      separatorIcon,
      icon,
      rightAlign,
      title,
      titleBottom,
      card,
      heading,
      sx,
      others,
      divider
    });
    if (result) return result;
  }

  return <Typography />;
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any
};
